<template>
  <Link v-if="tag == 'Link'" ref="button" class="button" :class="[`button--${btnStyle} button--${size}`, { dense }, { rounded }, { circular }, { noHover }]" :href="href" replace preserve-state @start="navigate">
    <slot />
  </Link>
  <component v-else ref="button" :is="tag" :href="href" class="button" :class="[`button--${btnStyle} button--${size}`, { dense }, { rounded }, { circular }, { noHover }]" @click="$emit('click', $event)">
    <slot />
  </component>
</template>

<script>
import { computed, ref } from '@vue/composition-api';
import { Link } from '@inertiajs/inertia-vue';
import { notifyRoute } from '@/lego/utils/route/notifier';

export default {
  components: { Link },
  props: {
    btnStyle: {
      type: String,
      default: 'primary',
    },

    size: {
      type: String,
      default: 'lg',
    },

    dense: {
      type: Boolean,
      default: false,
    },

    rounded: {
      type: Boolean,
      default: false,
    },

    circular: {
      type: Boolean,
      default: false,
    },

    tag: {
      type: String,
      default: 'button',
    },

    href: {
      type: String,
      default: null,
    },

    noHover: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const button = ref(null);

    const click = () => {
      button.value.click();
    };

    const prevStepPath = computed(() => {
      const url = new URL(props.href);
      return url.pathname + window.location.search;
    });

    return {
      button,
      click,
      prevStepPath,
      navigate: () => {
        if (!!prevStepPath.value) {
          notifyRoute(prevStepPath.value);
        }
      },
    };
  },
};
</script>

<style lang="postcss" scoped>
.button {
  @apply rounded-md style-body-sm inline-block text-center cursor-pointer border-solid;

  &--brand {
    @apply bg-brand-primary-main border-brand-primary-main hover:bg-brand-primary-soft disabled:bg-brand-primary-soft hover:border-brand-primary-soft disabled:border-brand-primary-soft disabled:text-neutral-semidark;

    &.noHover {
      @apply hover:bg-brand-primary-main hover:border-brand-primary-main;
    }
  }
  &--superdark {
    @apply bg-neutral-superdark2 text-neutral-white border-thin border-neutral-superdark2 hover:border-neutral-dark hover:bg-neutral-dark disabled:text-neutral-light;

    &.noHover {
      @apply hover:bg-neutral-superdark hover:border-neutral-superdark;
    }
  }

  &--primary {
    @apply bg-brand-secondary-dark text-neutral-white hover:bg-brand-secondary-lightest disabled:text-neutral-light;

    &.noHover {
      @apply hover:bg-brand-secondary-dark;
    }
  }
  &--secondary {
    @apply bg-transparent border-thin text-brand-secondary-dark border-brand-secondary-dark hover:border-brand-secondary-lightest hover:text-brand-secondary-lightest box-border disabled:text-neutral-medium disabled:border-neutral-medium;

    &.noHover {
      @apply hover:border-brand-secondary-dark hover:text-brand-secondary-dark;
    }
  }
  &--tertiary {
    @apply bg-neutral-superdark text-neutral-lighter disabled:opacity-50 disabled:pointer-events-none;
  }
  &--ghost {
    @apply bg-transparent text-brand-secondary-dark hover:text-brand-secondary-lightest border-transparent box-border hover:opacity-50;

    &.noHover {
      @apply hover:text-brand-secondary-dark hover:opacity-100;
    }
  }
  &--white {
    @apply bg-neutral-lighter text-neutral-ultradark hover:text-neutral-dark border-thin border-neutral-ultradark box-border hover:opacity-50;

    &.noHover {
      @apply hover:text-neutral-ultradark hover:opacity-100;
    }
  }
  &--grey {
    @apply bg-neutral-superdark bg-opacity-70 hover:bg-neutral-darkest text-neutral-white border-thin border-neutral-white box-border;

    &.noHover {
      @apply hover:bg-neutral-superdark;
    }
  }
  &--link {
    @apply underline hover:cursor-pointer text-neutral-darker;
  }
  &.rounded {
    @apply !rounded-pill;
  }
  &.circular {
    @apply !rounded-circular !min-w-fit;
  }
  &--xs {
    &:not(.dense) {
      @apply px-xxxs py-quark leading-long text-xs;
    }
    &.circular {
      @apply p-nano;
    }
  }
  &--sm {
    &:not(.dense) {
      @apply px-nano py-quark min-w-[10rem] leading-long text-nano;
    }
    &.circular {
      @apply p-micro;
    }
  }
  &--md {
    &:not(.dense) {
      @apply px-xxxs py-nano min-w-[10rem] leading-long text-xs;
    }
    &.circular {
      @apply p-xxxs;
    }
  }
  &--lg {
    &:not(.dense) {
      @apply px-xxs py-xxxs leading-medium text-sm;
    }
    &.circular {
      @apply p-xxs;
    }
  }
}
</style>
